import { queryOptions, skipToken, useQuery } from '@tanstack/react-query'
import {
  fetchAlcoholComplianceTerms,
  fetchCMSPDPAdPlacement,
  fetchDeferredHydration,
  fetchOrderPlacedCMSShelves,
  fetchPdpCMSShelves,
  fetchSearchCMSAdBanner,
  fetchSearchCMSAdShowcase,
  fetchZeroResultsCMSShelves,
  fetchComplementaryShelf,
  fetchSearchCMSAdVideoShowcase,
  fetchPostCartUpsellShelves,
  fetchCartPageCMSShelves,
} from '@/features/cms/services/CMS/fetchers'
import { getStateTermData } from '@/features/cms/services/CMS/utils'
import { useCMSParamsWithoutSlug } from '@/features/cms/services/CMS/hooks'
import { type Content, type ContentAliasMap } from '@/features/cms/components/CMS/types'
import { datadogRum } from '@datadog/browser-rum'
import { useEffect } from 'react'
import { useRetailerContext } from '@/features/shared/context/RetailerContext'
import {
  catNavCmsQueryOptions,
  cmsContentAliasQueryOptions,
  cmsQueryOptions,
  cmsGlobalHeaderCategoriesOptions,
  type CmsQueryOptionsParams,
} from '@/features/cms/services/CMS/queryOptions'
import { useUser } from '@/features/account/services/User/hooks'

export const CMS_RHP_SLUG = 'retailer_homepage'
export const CMS_GHP_SLUG = 'global_homepage'
export const CMS_DEALS_SLUG = 'shop/deals'

export const useQueryCMS = ({
  params,
  enabled,
  keepPreviousData,
  enableDeferredHydration = true,
  trackPageContentVersion,
}: {
  params: ReturnType<typeof useCMSParams>
  enabled: boolean
  keepPreviousData?: boolean
  enableDeferredHydration?: boolean
  trackPageContentVersion?: boolean
}) => {
  const queryResult = useQuery({
    ...cmsQueryOptions({ ...params, enableDeferredHydration }),
    enabled,
    placeholderData: (prevData, previousQuery) =>
      keepPreviousData &&
      // we only want to keepPreviousData if the user has not changed their address
      // this prevents stale data from rendering on the GHP
      previousQuery?.queryKey[1].addressParams?.address_line_1 ===
        params.addressParams?.address_line_1
        ? prevData
        : undefined,
  })

  useEffect(() => {
    if (queryResult.data && trackPageContentVersion) {
      try {
        const {
          id,
          name,
          metadata: { version },
        } = queryResult.data.content

        datadogRum.setViewContextProperty('cmsPageContent', {
          id,
          name,
          version,
        })
      } catch (error) {
        // do nothing if error occurs
      }
    }
  }, [queryResult.data, trackPageContentVersion])

  return queryResult
}

export const useQueryCatNavCMS = (enabled: boolean = true) => {
  const params = useCMSParamsWithoutSlug()
  return useQuery({
    ...catNavCmsQueryOptions(params),
    enabled: Boolean(enabled && params.storeParams.store_location_id),
  })
}

export const useCMSParams = (slug: string) => {
  const cmsParamsWithoutSlug = useCMSParamsWithoutSlug()
  const retailerContext = useRetailerContext()

  return {
    ...cmsParamsWithoutSlug,
    slug,
    ...(retailerContext?.storeName && {
      inputVariables: {
        retailer_name: retailerContext.storeName,
      },
    }),
  }
}

export const deferredHydrationQueryOptions = (hydrationCallback: Content['hydration_callback']) =>
  queryOptions({
    queryKey: ['DeferredCallback', hydrationCallback],
    queryFn: hydrationCallback ? () => fetchDeferredHydration(hydrationCallback) : skipToken,
  })

const orderPlacedCMSShelvesOptions = ({
  exclude_product_ids,
  ...params
}: CmsQueryOptionsParams & {
  exclude_product_ids: number[]
}) =>
  // exclude_product_ids is excluded from the query key since we only care about
  // passing it to the query function in the initial fetch
  // oxlint-disable-next-line @tanstack/query/exhaustive-deps
  queryOptions({
    queryKey: ['CMS_ORDER_PLACED_SHELVES', params] as const,
    queryFn: (context) => fetchOrderPlacedCMSShelves(context, exclude_product_ids),
  })

export const useQueryOrderPlacedCMSShelves = ({
  exclude_product_ids,
}: {
  exclude_product_ids: number[]
}) => {
  const params = useCMSParamsWithoutSlug()

  return useQuery({
    ...orderPlacedCMSShelvesOptions({
      exclude_product_ids,
      ...params,
    }),
    enabled: Boolean(params.addressParams?.address_id && exclude_product_ids.length > 0),
  })
}

const cartPageCMSShelvesOptions = ({
  exclude_product_ids,
  ...params
}: CmsQueryOptionsParams & {
  exclude_product_ids: number[]
}) =>
  // exclude_product_ids is excluded from the query key since we only care about
  // passing it to the query function in the initial fetch
  // oxlint-disable-next-line @tanstack/query/exhaustive-deps
  queryOptions({
    queryKey: ['CMS_CART_PAGE_SHELVES', params] as const,
    queryFn: (context) => fetchCartPageCMSShelves(context, exclude_product_ids),
  })

export const useQueryCartPageCMSShelves = ({
  exclude_product_ids,
}: {
  exclude_product_ids: number[]
}) => {
  const params = useCMSParamsWithoutSlug()

  return useQuery({
    ...cartPageCMSShelvesOptions({
      exclude_product_ids,
      ...params,
    }),
    // Refetch on mount is needed to get latest recommendation when the user lands on the cart page,
    // and if they navigate away, add/remove items elsewhere, then return.
    refetchOnMount: 'always',
    enabled: Boolean(params.storeParams.store_location_id && exclude_product_ids.length > 0),
  })
}

const postCartUpsellShelvesOptions = ({
  exclude_product_ids,
  ...params
}: CmsQueryOptionsParams & {
  exclude_product_ids: number[]
}) =>
  // exclude_product_ids is excluded from the query key since we only care about
  // passing it to the query function in the initial fetch
  // oxlint-disable-next-line @tanstack/query/exhaustive-deps
  queryOptions({
    queryKey: ['CMS_POST_CART_UPSELL_SHELVES', params] as const,
    queryFn: (context) => fetchPostCartUpsellShelves(context, exclude_product_ids),
  })

export const useQueryPostCartUpsellShelves = ({
  exclude_product_ids,
  enabled,
}: {
  exclude_product_ids: number[]
  enabled: boolean
}) => {
  const params = useCMSParamsWithoutSlug()

  return useQuery({
    ...postCartUpsellShelvesOptions({
      exclude_product_ids,
      ...params,
    }),
    enabled: enabled && exclude_product_ids.length > 0,
  })
}

const zeroSearchResultsCMSShelvesOptions = (
  params: CmsQueryOptionsParams & {
    query: string
  },
) =>
  queryOptions({
    queryKey: ['CMS_ZERO_RESULTS_SHELVES', params] as const,
    queryFn: fetchZeroResultsCMSShelves,
  })

export const useQueryZeroSearchResultsCMSShelves = (query: string, enabled: boolean) => {
  const params = useCMSParamsWithoutSlug()

  return useQuery({
    ...zeroSearchResultsCMSShelvesOptions({
      ...params,
      query,
    }),
    enabled,
  })
}

const alcoholComplianceTermsOptions = (params: CmsQueryOptionsParams) =>
  queryOptions({
    queryKey: ['CMS_ALCOHOL_COMPLIANCE_TERMS', params] as const,
    queryFn: fetchAlcoholComplianceTerms,
  })

export const useQueryAlcoholComplianceTerms = (enabled: boolean) => {
  const params = useCMSParamsWithoutSlug()

  return useQuery({
    ...alcoholComplianceTermsOptions(params),
    select: (data) =>
      getStateTermData(data.data.terms_by_state, params.addressParams?.address_state),
    enabled: Boolean(enabled && params.addressParams?.address_state),
  })
}

const searchCMSAdBannerOptions = (
  params: CmsQueryOptionsParams & { category_id?: number; query?: string },
) =>
  queryOptions({
    queryKey: ['CMS_SEARCH_AD_BANNER', params] as const,
    queryFn: fetchSearchCMSAdBanner,
  })

export const useQuerySearchCMSAdBanner = ({
  category_id,
  query,
}: {
  category_id?: number
  query?: string
}) => {
  const params = useCMSParamsWithoutSlug()
  const isEligibleToQuery = Boolean(params.storeParams.store_location_id && (query || category_id))

  return useQuery({
    ...searchCMSAdBannerOptions({
      category_id,
      query,
      ...params,
    }),
    enabled: isEligibleToQuery,
  })
}

const searchCMSAdShowcaseOptions = (
  params: CmsQueryOptionsParams & { category_id?: number; query?: string },
) =>
  queryOptions({
    queryKey: ['CMS_SEARCH_AD_SHOWCASE', params] as const,
    queryFn: fetchSearchCMSAdShowcase,
  })

export const useQuerySearchCMSAdShowcase = ({
  category_id,
  query,
}: {
  category_id?: number
  query?: string
}) => {
  const params = useCMSParamsWithoutSlug()
  const isEligibleToQuery = Boolean(params.storeParams.store_location_id && (query || category_id))

  return useQuery({
    ...searchCMSAdShowcaseOptions({
      category_id,
      query,
      ...params,
    }),
    enabled: isEligibleToQuery,
  })
}

const cmsPdpShelvesQueryOptions = (
  params: CmsQueryOptionsParams & {
    product_id: number
  },
) =>
  queryOptions({
    queryKey: ['CMS_PRODUCT_DESCRIPTION_CMS_SHELVES', params] as const,
    queryFn: fetchPdpCMSShelves,
  })

export const useQueryCMSPdpShelves = (product_id: number) => {
  const params = useCMSParamsWithoutSlug()

  return useQuery({
    ...cmsPdpShelvesQueryOptions({
      ...params,
      product_id,
    }),
  })
}

const cmsPdpAdPlacementQueryOptions = (
  params: CmsQueryOptionsParams & {
    product_id: number
  },
) =>
  queryOptions({
    queryKey: ['CMS_PDP_AD_PLACEMENT', params] as const,
    queryFn: fetchCMSPDPAdPlacement,
  })

export const useQueryCMSPDPAdPlacement = (product_id: number, enabled: boolean = true) => {
  const params = useCMSParamsWithoutSlug()

  return useQuery({
    ...cmsPdpAdPlacementQueryOptions({
      ...params,
      product_id,
    }),
    enabled: enabled && Boolean(product_id),
  })
}

export const useQueryCMSGlobalHeaderCategories = (enabled: boolean) => {
  const params = useCMSParamsWithoutSlug()

  return useQuery({
    ...cmsGlobalHeaderCategoriesOptions(params),
    placeholderData: (prevData) => prevData,
    enabled: Boolean(enabled && params.addressParams?.zip),
  })
}

/**
 * Generic query hook used for CMS content aliases with no custom inputs.
 * When in doubt, use this query!
 */
export const useQueryCMSContentAlias = <
  T extends keyof ContentAliasMap,
  TData = ContentAliasMap[T],
>(
  contentAlias: T,
  {
    select,
    ...options
  }: {
    enabled: boolean
    select?: (data: ContentAliasMap[T]) => TData
    refetchOnMount?: boolean | 'always'
  } = {
    enabled: true,
  },
) => {
  const params = useCMSParamsWithoutSlug()
  return useQuery({
    ...cmsContentAliasQueryOptions(contentAlias, params),
    ...options,
    select,
  })
}

export const getComplementaryShelfQueryOptions = (
  params: CmsQueryOptionsParams & { product_id: number },
) =>
  queryOptions({
    queryKey: ['CMS_COMPLEMENTARY_SHELF', params] as const,
    queryFn: fetchComplementaryShelf,
  })

const cmsAdVideoShowcaseQueryOptions = (params: CmsQueryOptionsParams & { query: string }) =>
  queryOptions({
    queryKey: ['CMS_SEARCH_AD_VIDEO_SHOWCASE', params] as const,
    queryFn: fetchSearchCMSAdVideoShowcase,
  })

export const useQuerySearchAdVideoShowcase = ({ query }: { query: string }) => {
  const params = useCMSParamsWithoutSlug()

  return useQuery({
    ...cmsAdVideoShowcaseQueryOptions({ ...params, query }),
    enabled: Boolean(query && params.storeParams.store_location_id),
  })
}

export const useQueryCMSActivityPunchCard = (refetchOnMount?: 'always') => {
  const user = useUser()
  return useQueryCMSContentAlias('user-activity-punchcard', {
    enabled: Boolean(user?.id),
    refetchOnMount,
  })
}
